import { axiosInstance } from '../clients/axiosClient';
import {
  API_V1_PREFIX,
  MEMBERS_API_PREFIX,
} from '../utils/constants';
import { formatUKDate, getAccessToken } from '../utils';

const INVALID_FILENAME_CHARS = new RegExp(
  `[<>:"/\\\\|?*\\r\\n${String.fromCharCode(0)}]`,
  'g'
);

export const getPreScanQuestionnairePdfFilename = (
  memberName: string,
  submittedAt: unknown
): string => {
  const safeName = memberName.replace(INVALID_FILENAME_CHARS, '').trim() || 'Member';
  const submittedAtString = typeof submittedAt === 'string' ? submittedAt : null;
  const formattedDate = formatUKDate(submittedAtString, 'dd MMM yyyy');
  const datePart = formattedDate === '-' ? 'Unknown date' : formattedDate;
  return `${safeName} - PreScan Questionnaire - ${datePart}.pdf`;
};

export const getOnboardingFormPdfFilename = (
  memberName: string,
  submittedAt: unknown
): string => {
  const safeName = memberName.replace(INVALID_FILENAME_CHARS, '').trim() || 'Member';
  const submittedAtString = typeof submittedAt === 'string' ? submittedAt : null;
  const formattedDate = formatUKDate(submittedAtString, 'dd MMM yyyy');
  const datePart = formattedDate === '-' ? 'Unknown date' : formattedDate;
  return `${safeName} - Onboarding Form - ${datePart}.pdf`;
};

export const buildPreScanQuestionnairePdfUrl = (memberId: string): string | null => {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return null;
  }

  const baseUrl = axiosInstance.defaults.baseURL?.replace(/\/$/, '') ?? '';
  const path = `${API_V1_PREFIX}${MEMBERS_API_PREFIX}${memberId}/pre-scan-questionnaire-pdf`;
  const params = new URLSearchParams({ access_token: accessToken });
  return `${baseUrl}/${path}?${params.toString()}`;
};


type ResponseHeaders = Record<string, string | string[] | null | undefined>;

export const parseAxiosBlobError = async (error: any): Promise<any> => {
  const responseData = error?.response?.data;
  if (!(responseData instanceof Blob)) {
    return error;
  }

  try {
    const text = await responseData.text();
    const parsed = JSON.parse(text);
    if (parsed?.detail) {
      return {
        ...error,
        message: parsed.detail,
        response: { ...error.response, data: parsed },
      };
    }
  } catch {
    // Response was not JSON — keep original error.
  }

  return error;
};

export const getFilenameFromResponseHeaders = (
    headers: ResponseHeaders,
    fallback = 'download.pdf'
): string => {
    const xFileName = headers['x-file-name'];
    if (typeof xFileName === 'string' && xFileName.trim()) {
        return xFileName.trim();
    }

    const contentDisposition = headers['content-disposition'];
    if (typeof contentDisposition !== 'string') {
        return fallback;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1].trim());
        } catch {
            return utf8Match[1].trim();
        }
    }

    const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
    if (quotedMatch?.[1]) {
        return quotedMatch[1].trim();
    }

    const unquotedMatch = contentDisposition.match(/filename=([^;]+)/i);
    if (unquotedMatch?.[1]) {
        return unquotedMatch[1].trim();
    }

    return fallback;
};